import {
  Button,
  CloseButton,
  SettingsButton,
  SaveButton,
} from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import {
  DSI_TEMPLATE_ID,
  EUR_TEMPLATE_ID,
  extractSpecialRatioTags,
  isEurOrDsiTemplate,
} from '../../../../data/providers/reports/ratioReportProvider';
import {
  IRatioReportSettings,
  IRatioReportSettingsState,
  ISpecialReportOptions,
  ratioReportSettingsProvider,
} from '../../../../data/providers/settings/reports/ratioReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { IRange, ISelectableTag } from '../../../../data/types';
import {
  deleteSelectedTemplate,
  saveRatioSettings,
} from '../../../../data/writers/settings/reports/ratioReportSettings';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { TagSelect } from '../../../shared/form/selects/TagsSelect';
import { TimeframeSelect } from '../../../shared/form/selects/TimeframeSelect';
import { Step } from '../../../shared/form/Step';
import { Toggle } from '../../../shared/form/Toggle';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IRatioReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IRatioReportSettings>>
  >([]);
  const [initialTemplate, setInitialTemplate] =
    React.useState<ISettingsTemplate<IRatioReportSettings>>();

  const [metricOneTags, setMetricOneTags] = React.useState<ISelectableTag[]>(
    []
  );
  const [metricTwoTags, setMetricTwoTags] = React.useState<ISelectableTag[]>(
    []
  );

  const [settingsState, setSettingsState] =
    React.useState<IRatioReportSettingsState>();

  const [timeframe, setTimeframe] = React.useState<IRange>();
  const [filter, setFilter] = React.useState<boolean>();

  const [saving, setSaving] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * @description subscribe to ratio report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = ratioReportSettingsProvider().subscribe(
      (mSettings: IRatioReportSettingsState): void => {
        setSettingsState(mSettings);
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialTemplate(mSettings?.selected);

        if (!isEmpty(mSettings?.timeframe)) {
          setTimeframe(mSettings.timeframe);
        }

        if (mSettings?.filter !== undefined) {
          setFilter(mSettings.filter);
        }

        if (isEurOrDsiTemplate(mSettings?.selected?.id)) {
          setMetricOneTags(
            extractSpecialRatioTags(
              mSettings?.selected?.id,
              mSettings,
              'metricOneTags'
            ) ?? []
          );
          setMetricTwoTags(
            extractSpecialRatioTags(
              mSettings?.selected?.id,
              mSettings,
              'metricTwoTags'
            ) ?? []
          );
        }

        setLoading(false);
      }
    );

    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setSaving(true);

    if (!isEqual(initialTemplate?.id, selectedTemplate?.id)) {
      props.onSave();
    }
    let tags: { [key: string]: ISpecialReportOptions };

    if (selectedTemplate.id === EUR_TEMPLATE_ID) {
      tags = {
        eur: {
          metricOneTags,
          metricTwoTags,
        },
      };
    }

    if (selectedTemplate.id === DSI_TEMPLATE_ID) {
      tags = {
        dsi: {
          metricOneTags,
          metricTwoTags,
        },
      };
    }

    await saveRatioSettings(selectedTemplate, timeframe, filter, tags ?? null);

    setSaving(false);
    props.onClose();
  };

  /**
   * @description handle changes to timeframe
   * @param timeframe
   */
  const timeframeChangeHandler = (mTimeframe: IRange): void =>
    setTimeframe(mTimeframe);

  const dataFilterChangeHandler = (
    _: string | number,
    mFilter: boolean
  ): void => setFilter(mFilter);
  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IRatioReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  React.useEffect((): void => {
    if (isEurOrDsiTemplate(selectedTemplate?.id)) {
      setMetricOneTags(
        extractSpecialRatioTags(
          selectedTemplate?.id,
          settingsState,
          'metricOneTags'
        ) ?? []
      );
      setMetricTwoTags(
        extractSpecialRatioTags(
          selectedTemplate?.id,
          settingsState,
          'metricTwoTags'
        ) ?? []
      );
    }
  }, [selectedTemplate?.id, JSON.stringify(settingsState)]);

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IRatioReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IRatioReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IRatioReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  const isValid = React.useMemo((): boolean => {
    /// make sure user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// form is valid and can be submitted
    return true;
  }, [JSON.stringify(timeframe), JSON.stringify(selectedTemplate)]);

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Ratio Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading || saving ? (
            <LinearLoading />
          ) : (
            <React.Fragment>
              <Row shouldCollapse style={{ height: '100%' }}>
                <LeftColumn>
                  <TemplateSelect
                    step={1}
                    templates={templates}
                    onSelect={templateChangeHandler}
                    onDelete={templateDeleteHandler}
                    selectedTemplateId={selectedTemplate?.id}
                    formComponent={TemplateForm}
                    reportType={'Ratio Report'}
                    reportTypeId='ratioReport'
                  />
                </LeftColumn>
                <RightColumn>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>2</Step>
                        <h6>Select Timeframe</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <div style={{ padding: '10px' }}>
                      <ControlSpacer>
                        <TimeframeSelect
                          dateRange={timeframe}
                          onChange={timeframeChangeHandler}
                        />
                      </ControlSpacer>
                    </div>
                  </FormSection>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>3</Step>
                        <h6>Hide Incomplete Sessions</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <ControlSpacer>
                      <Toggle
                        name='filter'
                        label='Hide'
                        onChange={dataFilterChangeHandler}
                        value='filter'
                        checked={filter}
                      />
                    </ControlSpacer>
                  </FormSection>
                  {selectedTemplate.id === EUR_TEMPLATE_ID ||
                  selectedTemplate.id === DSI_TEMPLATE_ID ? (
                    <FormSection>
                      <CardHeader>
                        <CardHeaderLeft>
                          <Step>4</Step>
                          <h6>Add Tags</h6>
                        </CardHeaderLeft>
                      </CardHeader>
                      <div style={{ padding: '10px' }}>
                        <Column>
                          <Row shouldCollapse>
                            <LeftSide>
                              <ControlLabel>CMJ Tags</ControlLabel>
                              <TagSelect
                                value={metricOneTags}
                                onChange={setMetricOneTags}
                              />
                            </LeftSide>
                            <RightSide>
                              <ControlLabel>
                                {selectedTemplate.id === DSI_TEMPLATE_ID
                                  ? 'Isometric Tags'
                                  : 'Squat Jump Tags'}
                              </ControlLabel>
                              <TagSelect
                                value={metricTwoTags}
                                onChange={setMetricTwoTags}
                              />
                            </RightSide>
                          </Row>
                        </Column>
                      </div>
                    </FormSection>
                  ) : null}
                  <p
                    style={{
                      marginTop: '15px',
                      fontSize: '1.2em',
                      color: 'var(--color-accent)',
                    }}
                  >
                    <a
                      rel='noreferrer'
                      target='_blank'
                      href='https://www.hawkindynamics.com/blog/ratio-report'
                    >
                      https://www.hawkindynamics.com/blog/ratio-report
                    </a>
                  </p>
                </RightColumn>
              </Row>
            </React.Fragment>
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton
              onClick={handleSave}
              disabled={loading || saving || !isValid}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const RatioSettings = withRouter((props: IProps): React.ReactElement => {
  const open = (): void =>
    openPopup(
      <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
      props.history,
      'ratio-report-settings'
    );

  return <SettingsButton onClick={open} />;
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LeftSide = styled(ControlSpacer as any)`
  flex: 1;
  margin-right: 10px;
  width: 100%;
  @media (max-width: 990px) {
    margin-right: 0;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RightSide = styled(ControlSpacer as any)`
  flex: 1;
  width: 100%;
`;
